import { parseISO } from "date-fns";
import { z } from "zod";

const RevisionSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const AppointmentManagementRequestSchema = z.object({ expectedRevision: RevisionSchema });
export const AppointmentManagementDtoSchema = z.object({ expected_revision: RevisionSchema });
const AppointmentManagementResultSchema = z.object({
  revision: RevisionSchema, expiresAt: z.date().nullable(),
});
export const AppointmentManagementLinkSchema = AppointmentManagementResultSchema.extend({ managementUrl: z.url() });
const AppointmentManagementResultDtoSchema = z.object({
  revision: RevisionSchema, expires_at: z.iso.datetime({ offset: true }).nullable(),
});
const AppointmentManagementLinkDtoSchema = AppointmentManagementResultDtoSchema.extend({ management_url: z.url() });

export type AppointmentManagementDto = z.infer<typeof AppointmentManagementDtoSchema>;
export type AppointmentManagementResultDto = z.infer<typeof AppointmentManagementResultDtoSchema>;
export type AppointmentManagementLinkDto = z.infer<typeof AppointmentManagementLinkDtoSchema>;
export type AppointmentManagementRequest = z.infer<typeof AppointmentManagementRequestSchema>;
export type AppointmentManagementResult = z.infer<typeof AppointmentManagementResultSchema>;
export type AppointmentManagementLink = z.infer<typeof AppointmentManagementLinkSchema>;

export function fromAppointmentManagementRequestToDto(request: AppointmentManagementRequest): AppointmentManagementDto {
  const data = AppointmentManagementRequestSchema.parse(request);
  return { expected_revision: data.expectedRevision };
}

export function fromAppointmentManagementResultDto(dto: AppointmentManagementResultDto): AppointmentManagementResult {
  const parsed = AppointmentManagementResultDtoSchema.safeParse(dto);
  if (!parsed.success) throw new Error("The management response could not be confirmed. Refresh before continuing.");
  return { revision: parsed.data.revision, expiresAt: parsed.data.expires_at ? parseISO(parsed.data.expires_at) : null };
}

export function fromAppointmentManagementLinkDto(dto: AppointmentManagementLinkDto): AppointmentManagementLink {
  // Never expose a credential-bearing validation error through mutation state.
  const parsed = AppointmentManagementLinkDtoSchema.safeParse(dto);
  if (!parsed.success) throw new Error("The management response could not be confirmed. Refresh before continuing.");
  return {
    managementUrl: parsed.data.management_url,
    revision: parsed.data.revision,
    expiresAt: parsed.data.expires_at ? parseISO(parsed.data.expires_at) : null,
  };
}
